import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Bot, MessageSquare, Zap, Shield, Target, TrendingUp, 
  Clock, CheckCircle, ArrowRight, Play, Users, Globe,
  FileText, DollarSign, Mail, Phone, Sparkles, RefreshCw
} from 'lucide-react';
import Layout from '@/components/Layout';
import SEO from '@/components/SEO';

export default function HowItWorks() {
  const navigate = useNavigate();
  const [activeExample, setActiveExample] = useState('inquiry');

  const features = [
    {
      id: 'ai-reply',
      icon: <Bot className="h-8 w-8" />,
      title: 'AI Auto-Reply',
      subtitle: 'Respond to buyers in under 5 minutes',
      description: 'When a buyer sends an inquiry, GlobalVyapar\'s AI immediately crafts a professional, personalized response. No more missed opportunities due to delayed replies.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'negotiation',
      icon: <DollarSign className="h-8 w-8" />,
      title: 'AI Price Negotiation',
      subtitle: 'Set your floor price, AI handles the rest',
      description: 'You define your minimum acceptable price. The AI negotiates with buyers professionally, never dropping below your floor price. You stay in control while AI does the work.',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      id: 'strategy',
      icon: <Target className="h-8 w-8" />,
      title: 'Strategy Bands',
      subtitle: 'Choose your communication style',
      description: 'Select from Professional, Friendly, or Urgent tones. The AI adapts its messaging style to match your brand voice and the situation.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 'drafts',
      icon: <FileText className="h-8 w-8" />,
      title: 'AI Draft Generation',
      subtitle: 'Professional messages in seconds',
      description: 'Generate follow-up emails, quotations, and responses with one click. Review and customize before sending, or let AI handle it automatically.',
      color: 'from-orange-500 to-amber-500'
    },
    {
      id: 'verification',
      icon: <Shield className="h-8 w-8" />,
      title: 'Buyer Verification',
      subtitle: 'Only deal with legitimate businesses',
      description: 'Every buyer is verified through GSTIN and IEC codes. Spend your time on real opportunities, not tire-kickers.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 'followup',
      icon: <RefreshCw className="h-8 w-8" />,
      title: '30-Day Auto Follow-up',
      subtitle: 'Never let a lead go cold',
      description: 'AI automatically follows up with inactive leads for 30 days. Track every conversation in your pipeline dashboard.',
      color: 'from-indigo-500 to-violet-500'
    }
  ];

  const examples = {
    inquiry: {
      title: 'Buyer Inquiry → AI Response',
      buyer: {
        name: 'Ahmed Hassan',
        company: 'Dubai Spice Trading LLC',
        message: 'Hello, I am interested in your cumin seeds. What is your best price for 2 MT? We need delivery to Dubai.'
      },
      aiResponse: `Dear Mr. Hassan,

Thank you for your interest in our premium cumin seeds. We appreciate you reaching out to Unjha Agro Exports.

For your requirement of 2 MT cumin seeds with delivery to Dubai, we can offer:

• Grade: Singapore Quality (99% purity)
• Price: USD 2,450/MT CFR Dubai
• Delivery: 15-20 days from order confirmation
• Payment: 30% advance, 70% against B/L copy

Our cumin seeds are machine-cleaned, double-sorted with moisture below 8%. All export documentation including phytosanitary certificate will be arranged.

Would you like to proceed with a sample shipment first, or shall I send you our detailed product specification sheet?

Best regards,
Unjha Agro Exports`
    },
    negotiation: {
      title: 'Price Negotiation Example',
      setup: {
        floorPrice: 'USD 2,300/MT',
        targetPrice: 'USD 2,500/MT',
        strategy: 'Professional'
      },
      conversation: [
        { role: 'buyer', message: 'Your price is too high. We can get cumin at USD 2,200/MT from another supplier.' },
        { role: 'ai', message: 'Thank you for sharing that, Mr. Hassan. While we understand price is important, our cumin seeds offer 99% purity with consistent quality that many buyers find reduces rejection rates. For a valued partner like yourself, we can offer USD 2,400/MT for orders above 5 MT. This includes all quality certifications.' },
        { role: 'buyer', message: 'Can you do USD 2,250/MT? That\'s our budget.' },
        { role: 'ai', message: 'I appreciate your transparency. Our absolute best price for premium grade is USD 2,350/MT, which already includes phytosanitary certification and quality testing. This is a special rate we reserve for serious long-term buyers. Shall I proceed with the proforma invoice?' }
      ],
      note: 'The AI never went below the floor price of USD 2,300/MT while maintaining a professional relationship.'
    },
    strategy: {
      title: 'Strategy Bands in Action',
      scenario: 'Follow-up after 3 days of no response',
      bands: [
        {
          name: 'Professional',
          color: 'bg-blue-100 text-blue-700',
          message: 'Dear Mr. Hassan, I wanted to follow up on our conversation regarding cumin seeds. Please let me know if you have any questions or need additional information to proceed.'
        },
        {
          name: 'Friendly',
          color: 'bg-green-100 text-green-700',
          message: 'Hi Ahmed! Just checking in about those cumin seeds. 😊 Let me know if there\'s anything I can help clarify. Looking forward to working together!'
        },
        {
          name: 'Urgent',
          color: 'bg-orange-100 text-orange-700',
          message: 'Mr. Hassan, our current batch of premium cumin is moving fast with limited stock remaining. To secure the quoted price and availability, I recommend confirming by Friday. Shall I reserve your quantity?'
        }
      ]
    }
  };

  return (
    <Layout>
      <SEO 
        title="How It Works"
        description="Learn how GlobalVyapar's AI-powered features help exporters respond faster, negotiate smarter, and close more deals with global buyers."
        keywords="AI export platform, automated buyer response, price negotiation AI, export CRM"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <section className="py-16 px-4 text-center">
          <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-100">
            <Sparkles className="h-3 w-3 mr-1" />
            AI-Powered Export Platform
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            How GlobalVyapar Works
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            From buyer inquiry to closed deal — see how our AI handles everything 
            so you can focus on growing your export business.
          </p>
          <div className="flex justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={() => navigate('/auth?tab=register&role=seller')}
            >
              Start Exporting <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
            >
              <Play className="mr-2 h-4 w-4" /> See Features
            </Button>
          </div>
        </section>

        {/* Process Overview */}
        <section className="py-12 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-12">The GlobalVyapar Process</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: '1', title: 'Buyer Inquires', desc: 'A global buyer finds your products and sends an inquiry', icon: <MessageSquare className="h-6 w-6" /> },
                { step: '2', title: 'AI Responds', desc: 'Within 5 minutes, AI sends a professional, personalized reply', icon: <Bot className="h-6 w-6" /> },
                { step: '3', title: 'AI Negotiates', desc: 'AI handles price discussions within your set boundaries', icon: <DollarSign className="h-6 w-6" /> },
                { step: '4', title: 'You Close', desc: 'Review the qualified lead and finalize the deal', icon: <CheckCircle className="h-6 w-6" /> }
              ].map((item, i) => (
                <div key={i} className="relative">
                  <Card className="text-center p-6 h-full">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4 text-teal-600">
                      {item.icon}
                    </div>
                    <div className="absolute -top-3 -left-3 w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {item.step}
                    </div>
                    <h3 className="font-semibold mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </Card>
                  {i < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                      <ArrowRight className="h-6 w-6 text-slate-300" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section id="features" className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">Powerful AI Features</h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Every feature is designed to save you time and close more deals
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => (
                <Card key={feature.id} className="group hover:shadow-lg transition-all cursor-pointer overflow-hidden"
                  onClick={() => {
                    setActiveExample(feature.id === 'ai-reply' ? 'inquiry' : feature.id === 'negotiation' ? 'negotiation' : 'strategy');
                    document.getElementById('examples').scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <CardHeader>
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                      {feature.icon}
                    </div>
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                    <CardDescription>{feature.subtitle}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Interactive Examples */}
        <section id="examples" className="py-16 px-4 bg-slate-900 text-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">See It In Action</h2>
            <p className="text-slate-400 text-center mb-8">
              Real examples of how GlobalVyapar AI works
            </p>
            
            <Tabs value={activeExample} onValueChange={setActiveExample} className="w-full">
              <TabsList className="grid w-full grid-cols-3 bg-slate-800 mb-8">
                <TabsTrigger value="inquiry" className="data-[state=active]:bg-teal-600">
                  <Bot className="h-4 w-4 mr-2" /> AI Response
                </TabsTrigger>
                <TabsTrigger value="negotiation" className="data-[state=active]:bg-teal-600">
                  <DollarSign className="h-4 w-4 mr-2" /> Negotiation
                </TabsTrigger>
                <TabsTrigger value="strategy" className="data-[state=active]:bg-teal-600">
                  <Target className="h-4 w-4 mr-2" /> Strategy Bands
                </TabsTrigger>
              </TabsList>

              <TabsContent value="inquiry">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Buyer Message */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                          A
                        </div>
                        <div>
                          <CardTitle className="text-white text-base">{examples.inquiry.buyer.name}</CardTitle>
                          <CardDescription className="text-slate-400">{examples.inquiry.buyer.company}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-slate-700 rounded-lg p-4">
                        <p className="text-slate-200">{examples.inquiry.buyer.message}</p>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">Received just now</p>
                    </CardContent>
                  </Card>

                  {/* AI Response */}
                  <Card className="bg-gradient-to-br from-teal-900 to-teal-800 border-teal-700">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
                          <Bot className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-white text-base flex items-center gap-2">
                            AI Response
                            <Badge className="bg-teal-500/30 text-teal-300 text-xs">Auto-generated</Badge>
                          </CardTitle>
                          <CardDescription className="text-teal-300">Sent in under 5 minutes</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-teal-950/50 rounded-lg p-4 max-h-64 overflow-y-auto">
                        <pre className="text-teal-100 text-sm whitespace-pre-wrap font-sans">{examples.inquiry.aiResponse}</pre>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="negotiation">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">{examples.negotiation.title}</CardTitle>
                    <div className="flex gap-4 mt-4">
                      <Badge className="bg-red-500/20 text-red-300">Floor: {examples.negotiation.setup.floorPrice}</Badge>
                      <Badge className="bg-green-500/20 text-green-300">Target: {examples.negotiation.setup.targetPrice}</Badge>
                      <Badge className="bg-blue-500/20 text-blue-300">Strategy: {examples.negotiation.setup.strategy}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {examples.negotiation.conversation.map((msg, i) => (
                      <div key={i} className={`flex ${msg.role === 'ai' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-lg p-4 ${
                          msg.role === 'ai' 
                            ? 'bg-teal-600 text-white' 
                            : 'bg-slate-700 text-slate-200'
                        }`}>
                          <p className="text-sm">{msg.message}</p>
                        </div>
                      </div>
                    ))}
                    <div className="mt-4 p-4 bg-emerald-900/30 rounded-lg border border-emerald-700">
                      <p className="text-emerald-300 text-sm flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        {examples.negotiation.note}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="strategy">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">{examples.strategy.title}</CardTitle>
                    <CardDescription className="text-slate-400">
                      Scenario: {examples.strategy.scenario}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {examples.strategy.bands.map((band, i) => (
                      <div key={i} className="p-4 bg-slate-700 rounded-lg">
                        <Badge className={`mb-3 ${band.color}`}>{band.name}</Badge>
                        <p className="text-slate-200">{band.message}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Export Business?</h2>
          <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
            Join thousands of exporters using AI to respond faster, negotiate smarter, and close more deals.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Button 
              size="lg" 
              className="bg-white text-teal-700 hover:bg-slate-100"
              onClick={() => navigate('/auth?tab=register&role=seller')}
            >
              Start Free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => navigate('/contact')}
            >
              <Phone className="mr-2 h-4 w-4" /> Book a Demo
            </Button>
          </div>
        </section>
      </div>
    </Layout>
  );
}
